import React, {
  createContext, ReactNode, useContext, useMemo, useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  InvitationUserItem,
  InviteUsersSubmitProps, useInviteUsersBulk,
  UsersInviteTableData,
  useUsersInviteTableGet,
} from '../hooks/api/inviteUsers';
import {
  DefaultFetchError, FetchCreate, FetchGet, FetchSuccess, PagingDataResponse,
} from '../hooks/fetch';
import { queryFilterParams } from '../utils';
import { useMessageError } from '../hooks/common';

type UsersInviteGetType = FetchGet<PagingDataResponse<InvitationUserItem>, any, DefaultFetchError, UsersInviteTableData>
type UserInviteBulkType = FetchCreate<FetchSuccess, DefaultFetchError, InviteUsersSubmitProps>

interface InviteUsersContextProps {
  isSubmitActive: boolean;
  handleIsSubmitActive: (payload: boolean) => void;
  usersInviteTableGet: UsersInviteGetType;
  // Promise<UsersInviteTableData | null>;
  fetchUsersInviteTable: (values: Partial<InviteUsersSubmitProps>, clearParams?: boolean) => void;
  tableReloadTriggerKey: number;
  setTableReloadTrigger: (val: number) => void;

  inviteUsersBulk: UserInviteBulkType;
}

const defaultValue: InviteUsersContextProps = {
  isSubmitActive: false,
  handleIsSubmitActive: () => undefined,
  usersInviteTableGet: {} as UsersInviteGetType,
  fetchUsersInviteTable: () => null,
  tableReloadTriggerKey: 0,
  setTableReloadTrigger: () => undefined,

  inviteUsersBulk: {} as UserInviteBulkType,
};

export const InviteUsersContext = createContext<InviteUsersContextProps>(defaultValue);

function InviteUsersProvider({ children }: { children: ReactNode }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isSubmitActive, setIsSubmitActive] = useState<boolean>(true);
  const [tableReloadTriggerKey, setTableReloadTrigger] = useState<number>(0);

  const usersInviteTableGet = useUsersInviteTableGet();
  const inviteUsersBulk = useInviteUsersBulk();

  const handleIsSubmitActive = (payload: boolean) => {
    setIsSubmitActive(payload);
  };

  const fetchUsersInviteTable = (values: Partial<InviteUsersSubmitProps>, clearParams = false) => {
    const newParams = {
      ...(Object.fromEntries(searchParams.entries()) || {}),
      ...({
        ...values,
      }),
      limit: undefined,
      sendEmailType: undefined,
    };

    setSearchParams(clearParams ? {} : queryFilterParams(newParams));
    handleIsSubmitActive(true); // make button SEND active

    setTableReloadTrigger(Date.now());
  };

  useMessageError([usersInviteTableGet, inviteUsersBulk]);

  const memoizedValue = useMemo(() => ({
    usersInviteTableGet,
    isSubmitActive,
    handleIsSubmitActive,
    fetchUsersInviteTable,
    tableReloadTriggerKey,
    setTableReloadTrigger,
    inviteUsersBulk,
  }), [usersInviteTableGet, isSubmitActive, handleIsSubmitActive, fetchUsersInviteTable,
    tableReloadTriggerKey, setTableReloadTrigger, inviteUsersBulk]);

  return (
    <InviteUsersContext.Provider value={memoizedValue}>
      {children}
    </InviteUsersContext.Provider>
  );
}

export default InviteUsersProvider;

export const useInviteUsersContext = () => {
  const context = useContext(InviteUsersContext);

  if (!context) {
    throw new Error(
      'useInviteUsersContext must be used within a InviteUsersContext',
    );
  }

  return context;
};
